import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Droplet,
    Settings,
    Thermometer,
    Zap,
    Disc,
    Wind,
    ArrowRight,
    Gauge,
    Info
} from 'lucide-react';
import { api, MaintenanceSpec } from '../lib/api';

interface ConsumablesPilesProps {
    vehicleContext?: {
        make?: string;
        model?: string;
        year?: number;
        engine?: string;
    };
}

interface PileItem {
    id: string;
    label: string;
    value: string;
    subValue?: string;
    icon: any;
    category: string;
    color: string;
}

export default function ConsumablesPiles({ vehicleContext }: ConsumablesPilesProps) {
    const navigate = useNavigate();
    const [spec, setSpec] = useState<MaintenanceSpec | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        async function fetchSpec() {
            if (!vehicleContext?.make || !vehicleContext?.model) return;

            setLoading(true);
            try {
                const data = await api.getMaintenanceSpec({
                    make: vehicleContext.make,
                    model: vehicleContext.model,
                    year: vehicleContext.year,
                    engine: vehicleContext.engine
                });
                if (data.success && data.spec) {
                    setSpec(data.spec);
                }
            } catch (error) {
                console.error('Failed to fetch maintenance specs:', error);
            } finally {
                setLoading(false);
            }
        }

        fetchSpec();
    }, [vehicleContext]);

    const piles: PileItem[] = [
        {
            id: 'oil',
            label: 'Engine Oil',
            value: spec?.oil_type || '5W-30',
            subValue: spec?.oil_capacity || '4.5 qt',
            icon: Droplet,
            category: 'Engine Core',
            color: 'from-amber-500/20 to-amber-600/5'
        },
        {
            id: 'transmission',
            label: 'Transmission',
            value: spec?.transmission_fluid || 'ATF Fluid',
            subValue: 'Drivetrain',
            icon: Settings,
            category: 'Drivetrain',
            color: 'from-red-500/20 to-red-600/5'
        },
        {
            id: 'coolant',
            label: 'Coolant',
            value: spec?.coolant_type || 'HOAT / OAT',
            subValue: 'Thermal',
            icon: Thermometer,
            category: 'Thermal',
            color: 'from-blue-500/20 to-blue-600/5'
        },
        {
            id: 'spark_plug',
            label: 'Spark Plug',
            value: spec?.spark_plug_type || 'Iridium / Platinum',
            subValue: 'Ignition',
            icon: Zap,
            category: 'Ignition',
            color: 'from-yellow-400/20 to-yellow-500/5'
        },
        {
            id: 'spark_gap',
            label: 'Spark Gap',
            value: spec?.spark_plug_gap || '0.044"',
            subValue: 'Precision',
            icon: Gauge,
            category: 'Precision',
            color: 'from-cyan-400/20 to-cyan-500/5'
        },
        {
            id: 'brake',
            label: 'Brake Fluid',
            value: spec?.brake_fluid || 'DOT 3 / 4',
            subValue: 'Hydraulic',
            icon: Disc,
            category: 'Hydraulic',
            color: 'from-emerald-500/20 to-emerald-600/5'
        },
        {
            id: 'steering',
            label: 'Steering',
            value: spec?.power_steering_fluid || 'EPSF / ATF',
            subValue: 'Control',
            icon: Info,
            category: 'Control',
            color: 'from-purple-500/20 to-purple-600/5'
        },
        {
            id: 'wipers',
            label: 'Wiper Sizes',
            value: spec?.wiper_sizes?.join(' / ') || 'Front / Rear',
            subValue: 'Visibility',
            icon: Wind,
            category: 'Visibility',
            color: 'from-slate-400/20 to-slate-500/5'
        }
    ];

    const handlePileClick = (pile: PileItem) => {
        const v = vehicleContext || {};
        const vehicleStr = v.make ? `${v.year || ''} ${v.make} ${v.model || ''} ${v.engine || ''}`.trim() : '';
        const query = `${pile.label} ${pile.value} ${vehicleStr}`.trim();
        navigate('/parts', { state: { initialSearch: query, vehicleContext: v } });
    };

    if (loading) {
        return (
            <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide">
                {[...Array(5)].map((_, i) => (
                    <div key={i} className="min-w-[160px] h-32 bg-slate-800/50 rounded-2xl animate-pulse" />
                ))}
            </div>
        );
    }

    return (
        <div className="w-full space-y-4 py-4">
            <div className="flex items-center justify-between px-2">
                <h3 className="text-[10px] uppercase tracking-[0.3em] font-black text-primary/80 flex items-center gap-2">
                    <Activity size={12} className="text-primary" />
                    Real-Time Maintenance Specs
                </h3>
                {spec && (
                    <span className="text-[9px] uppercase tracking-widest text-gray-500">
                        Source: {spec.source || 'AI Research Agent'}
                    </span>
                )}
            </div>

            <div className="flex gap-4 overflow-x-auto pb-6 px-2 scrollbar-hide snap-x">
                {piles.map((pile) => (
                    <button
                        key={pile.id}
                        onClick={() => handlePileClick(pile)}
                        className={`
              flex-shrink-0 w-40 h-32 relative group rounded-2xl border border-white/5 
              bg-gradient-to-br ${pile.color} backdrop-blur-md
              hover:border-primary/30 hover:shadow-[0_0_20px_rgba(var(--primary-rgb),0.1)]
              transition-all duration-500 text-left snap-start overflow-hidden
            `}
                    >
                        {/* Background Accent */}
                        <div className="absolute -right-4 -bottom-4 opacity-5 group-hover:opacity-10 transition-opacity duration-500">
                            <pile.icon size={80} />
                        </div>

                        <div className="h-full flex flex-col justify-between p-4 relative z-10">
                            <div className="space-y-1">
                                <div className="flex items-center justify-between">
                                    <div className="p-1.5 rounded-lg bg-white/5 border border-white/10 group-hover:border-primary/50 group-hover:text-primary transition-colors">
                                        <pile.icon size={16} />
                                    </div>
                                    <ArrowRight size={12} className="text-white/20 group-hover:text-primary group-hover:translate-x-1 transition-all" />
                                </div>
                                <div>
                                    <p className="text-[9px] uppercase tracking-wider text-gray-500 font-bold">{pile.category}</p>
                                    <p className="text-xs font-black text-white group-hover:text-primary transition-colors">{pile.label}</p>
                                </div>
                            </div>

                            <div className="space-y-0.5">
                                <p className="text-sm font-bold text-white leading-tight truncate">
                                    {pile.value}
                                </p>
                                {pile.subValue && (
                                    <p className="text-[10px] text-gray-400 font-medium">
                                        {pile.subValue}
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Hover Glitch Effect Line */}
                        <div className="absolute bottom-0 left-0 h-[2px] w-0 bg-primary group-hover:w-full transition-all duration-700" />
                    </button>
                ))}
            </div>
        </div>
    );
}

// Simple Activity icon for the header (internal to this file if lucide version is old)
function Activity({ size, className }: { size: number, className: string }) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={className}
        >
            <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
        </svg>
    )
}
